import React, { useEffect, useState } from 'react';

import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
} from '@mui/material';

import { useCreateReportingLeLens } from '~/hooks/apis/reporting/useGetReportingLeLens';
import { lensWriteFailureMessage } from '~/hooks/apis/reporting/useUpdateReportingLeLensErrors';

import type { Lens } from '~/types/leLens';

const MAX_LENS_TITLE_LENGTH = 200;
const COPY_SUFFIX = ' — Copy';

export function defaultLensCopyTitle(title: string): string {
  const base = title.trim() || 'Untitled Lens';
  return `${base.slice(0, MAX_LENS_TITLE_LENGTH - COPY_SUFFIX.length)}${COPY_SUFFIX}`;
}

export default function SaveLensAsDialog({
  onClose,
  onCreated,
  open,
  sourceLens,
}: {
  onClose: () => void;
  onCreated: (lens: Lens) => void;
  open: boolean;
  sourceLens: Lens | null;
}) {
  const createLens = useCreateReportingLeLens();
  const [title, setTitle] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open || !sourceLens) {
      return;
    }
    setTitle(defaultLensCopyTitle(sourceLens.title));
    setError(null);
  }, [open, sourceLens]);

  const close = () => {
    if (!createLens.isPending) {
      setError(null);
      onClose();
    }
  };

  const createCopy = async () => {
    const nextTitle = title.trim();
    if (!sourceLens || !nextTitle) {
      return;
    }
    setError(null);
    try {
      const { lens } = await createLens.mutateAsync({
        description: sourceLens.description,
        quietFailure: true,
        spec: sourceLens.spec,
        successMessage: 'Lens copied',
        title: nextTitle,
      });
      onCreated(lens);
    } catch (createError) {
      setError(lensWriteFailureMessage(createError, 'create'));
    }
  };

  return (
    <Dialog fullWidth maxWidth="sm" onClose={close} open={open}>
      <DialogTitle>Duplicate Lens</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>
          Create an independent draft from “{sourceLens?.title}”. Its cards, data, filters and layout will be copied;
          future changes will not affect the original.
        </DialogContentText>
        {error ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        ) : null}
        <TextField
          autoFocus
          disabled={createLens.isPending}
          fullWidth
          inputProps={{ maxLength: MAX_LENS_TITLE_LENGTH }}
          label="New Lens name"
          onChange={(event) => setTitle(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter' && title.trim()) {
              event.preventDefault();
              void createCopy();
            }
          }}
          value={title}
        />
      </DialogContent>
      <DialogActions>
        <Button disabled={createLens.isPending} onClick={close}>
          Cancel
        </Button>
        <Button disabled={createLens.isPending || !title.trim()} onClick={() => void createCopy()} variant="contained">
          {createLens.isPending ? 'Duplicating…' : 'Duplicate Lens'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
